import { ChangeDetectionStrategy, Component, Input, OnDestroy } from '@angular/core';
import { CommonModule } from '@angular/common';
import { UserSettingsService } from '../../../features/user-settings/state/user-settings.service';
import { UserSettingsQuery } from '../../../features/user-settings/state/user-settings.query';
import {
    distinctUntilChanged,
    filter,
    shareReplay,
    startWith,
    switchMap,
    takeUntil,
    takeWhile,
    tap
} from 'rxjs/operators';
import { SettingsScope } from '../../../features/user-settings/state/user-setting.model';
import { BehaviorSubject, combineLatest, Observable, of, Subject } from 'rxjs';
import { SharedModule } from '../../shared.module';
import { filterNilValue } from '@datorama/akita';
import { fadeInOut } from '../../animations/fade-in-out.animation';

@Component({
    selector: 'sb-pro-tip',
    standalone: true,
    imports: [CommonModule, SharedModule],
    templateUrl: './sb-pro-tip.component.html',
    styleUrls: ['./sb-pro-tip.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    animations: [fadeInOut]
})
export class SbProTipComponent implements OnDestroy {
    unsubscribe$ = new Subject();
    get fitToContainer(): boolean {
        return this._fitToContainer;
    }

    /** fitToContainer
     * @description Set width of the error alert to the width of the parent container
     * @type {boolean}
     */
    @Input() set fitToContainer(value: boolean) {
        this._fitToContainer = value;
    }

    private _fitToContainer = false;

    /** Force pro-tip to always be visible i.e. hide options to close and persist. */
    @Input() set alwaysVisible(value: boolean) {
        this.id = this.id || new Date().getTime().toString();
        this._alwaysVisible$.next(value);
    }

    /** Force hide pro-tip. */
    @Input() set forceHide(value: boolean) {
        this._forceHide$.next(value);
    }

    get alwaysVisible$(): Observable<boolean> {
        return this._alwaysVisible$.asObservable();
    }

    /** Scope of the pro-tip. Either 'COMPONENT' or 'ROUTE'. By default, it is set to 'COMPONENT' meaning that the pro-tip is tied to a component and if the tip is dismissed and the response persisted, it will be stored as component setting. If scope is set to 'ROUTE', the pro-tip is tied to the route instead of a specific component.
     */
    @Input() set scope(value: SettingsScope) {
        this._scope$.next(value);
    }
    get id$(): Observable<string> {
        return this._id$.asObservable().pipe(filterNilValue());
    }

    /** ID of the pro-tip. If scope is set to 'COMPONENT', the ID is used to identify the component. If scope is set to 'ROUTE', the ID is used to identify the route. */
    @Input() set id(value: string) {
        this._id$.next(value);
    }

    get id() {
        return this._id$.getValue();
    }
    private _id$ = new BehaviorSubject(null);

    get scope() {
        return this._scope$.getValue();
    }
    private _scope$: BehaviorSubject<SettingsScope> = new BehaviorSubject('COMPONENT');
    private _hideProTipTemporary$ = new BehaviorSubject(false);

    /** Heading of the pro-tip. */
    @Input() heading = 'Pro tip!';
    /** Content of pro-tip dismiss button. */
    @Input() dismissButtonText = 'Okay, got it!';

    private _forceHide$ = new BehaviorSubject(false);
    private _alwaysVisible$ = new BehaviorSubject(false);

    get isVisible() {
        return this._isVisible;
    }
    private _isVisible = false;

    hideProTip$ = combineLatest([
        this.id$,
        this._scope$,
        this._hideProTipTemporary$,
        this._forceHide$,
        this._alwaysVisible$
    ]).pipe(
        switchMap(([id, scope, hideTemporary, forceHide, alwaysVisible]) => {
            if (alwaysVisible) {
                return of(false);
            } else if (forceHide) {
                return of(true);
            } else if (hideTemporary) {
                return of(true);
            } else if (scope === 'COMPONENT') {
                return this._userSettingsQuery.COMPONENT.selectLoading().pipe(
                    takeWhile((loading) => loading, true),
                    shareReplay(1),
                    filter((loading) => !loading),
                    switchMap((_) => {
                        return this._userSettingsQuery.selectComponentSetting$(
                            'HIDE_PRO_TIP',
                            id
                        );
                    }),
                    startWith(true)
                );
            } else if (scope === 'ROUTE') {
                return this._userSettingsQuery.ROUTE.selectLoading().pipe(
                    takeWhile((loading) => loading, true),
                    shareReplay(1),
                    filter((loading) => !loading),
                    switchMap((_) => {
                        return this._userSettingsQuery.selectRouteSetting$(
                            'HIDE_PRO_TIP',
                            id
                        );
                    }),
                    startWith(true)
                );
            }
        }),
        startWith(true),
        distinctUntilChanged(),
        tap((isHidden) => {
            this._isVisible = !isHidden;
        }),
        shareReplay(1)
    );

    hideProTip(dontShowAgain: boolean) {
        if (!dontShowAgain) {
            this._hideProTipTemporary$.next(true);
        } else {
            if (!this.id) {
                throw new Error(
                    'ID must be set if dontShowAgain is true in order to persist setting.'
                );
            }
            if (this.scope === 'ROUTE') {
                this._userSettingsService
                    .saveRouteSetting('HIDE_PRO_TIP', true, this.id)
                    .pipe(takeUntil(this.unsubscribe$))
                    .subscribe();
            } else {
                this._userSettingsService
                    .saveComponentSetting('HIDE_PRO_TIP', true, this.id)
                    .pipe(takeUntil(this.unsubscribe$))
                    .subscribe();
            }
        }
    }
    constructor(
        private _userSettingsService: UserSettingsService,
        private _userSettingsQuery: UserSettingsQuery
    ) {}

    ngOnDestroy() {
        this.unsubscribe$.next(true);
        this.unsubscribe$.complete();
    }
}
